import axios from 'axios';
import { dashboardTabDataBody, emissionsFilters } from '@carboncare/schemas';
import { preflight } from './api/schemaPreflight';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance with authentication
const dashboardAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
dashboardAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
dashboardAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem('authToken');
      globalThis.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export const dashboardApi = {
  /**
   * Get dashboard header KPI data
   */
  getHeaderData: async (filters: any) => {
    preflight(emissionsFilters, filters ?? {});
    const response = await dashboardAxios.post('/dashboard/header-data', filters);
    return response.data;
  },

  /**
   * Get dashboard sub header data (transport modes, countries breakdown)
   */
  getSubHeaderData: async (filters: any) => {
    preflight(emissionsFilters, filters ?? {});
    const response = await dashboardAxios.post('/dashboard/dashboard-subgraph', filters);
    return response.data;
  },

  /**
   * Get dashboard emission chart data (monthly bar chart + totals)
   */
  getEmissionChartData: async (filters: any) => {
    preflight(emissionsFilters, filters ?? {});
    const response = await dashboardAxios.post('/dashboard/dashboard-emission-chart', filters);
    return response.data;
  },

  /**
   * Get available tabs for dashboard
   */
  getTabInfo: async () => {
    const response = await dashboardAxios.get('/dashboard/dashboard-tab-info');
    return response.data;
  },

  /**
   * Get tab data (emission tables)
   */
  getTabData: async (filters: any) => {
    preflight(dashboardTabDataBody, filters ?? {});
    const response = await dashboardAxios.post('/dashboard/tab-data', filters);
    return response.data;
  },

  /**
   * Get table dropdown options
   */
  getTableDropdownData: async () => {
    const response = await dashboardAxios.get('/dashboard/table-dropdown-data');
    return response.data;
  },

  /**
   * Get saved default filter for analytics/emissions page
   */
  getDefaultFilter: async (pageKey: string) => {
    const response = await dashboardAxios.get('/analytics/analytics-default-filter', {
      params: { page_key: pageKey },
    });
    return response.data;
  },

  /**
   * Save default filter for analytics/emissions page
   */
  setDefaultFilter: async (payload: { page_key: string; analytics_tab_id: string; time_period: string }) => {
    const response = await dashboardAxios.post('/analytics/analytics-default-filter', payload);
    return response.data;
  },

  /**
   * Get summary cards for the main dashboard (YTD, MTD)
   * Pass { companyId } in filters to scope results to a specific company.
   */
  getSummaryCards: async (filters: any) => {
    preflight(emissionsFilters, filters ?? {});
    const response = await dashboardAxios.post('/dashboard/summary-cards', filters);
    return response.data;
  },

  /**
   * Get detailed statistics for a specific company (used by LicenseeAdmin)
   */
  getCompanyStats: async (companyId: number, params?: { year?: number }) => {
    const response = await dashboardAxios.get(`/statistics/company/${companyId}`, { params });
    return response.data;
  },
};
